import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { get, isEmpty } from 'lodash-es'
import { subscribe } from 'react-contextual'
import classNames from 'classnames'
import { withFormik } from 'formik'
import { InvoicesProvider } from '../../../../providers'
import { CurrencyField, DimmerLoader, InvoiceCard, Segment, TextInput } from '../../..'
import PaymentAllocation from './PaymentAllocation/PaymentAllocation'
import TaskList from '../../../../components/atoms/Svgs/TaskList'
import styles from './ViewReconciledInvoice.module.scss'
import TextField from '../../../atoms/TextField/TextField'
import { pathOr } from 'ramda'
import { hashCode } from 'utils/string'
import { $TSFixMe } from 'types/ts-migrate'
import DocumentInfo from 'modules/documents/DocumentInfo/DocumentInfo'
import DownloadInvoicePdfButton from 'modules/invoices/DownloadInvoicePdfButton/DownloadInvoicePdfButton'
import CreditNotesList from 'modules/invoices/CreditNotesList/CreditNotesList'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import DownloadCreditNotePdfButton from 'modules/invoices/DownloadCreditNotePdfButton/DownloadCreditNotePdfButton'

interface ViewReconciledInvoiceProps {
  errors?: $TSFixMe
  currentInvoice?: $TSFixMe
  isHistoryInvoiceViewLoading?: boolean
  isReadOnly?: boolean
  getPartyNameById: (id: string) => string
  getInvoiceTypeByValue: (value: string) => $TSFixMe
  closeInvoice: (id: string, tab: string) => void
  updateBeneficiaries: (id: string, beneficiaries: $TSFixMe[]) => void
  // Injected by withFormik
  values: $TSFixMe
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void
  setFieldValue: (field: string, value: $TSFixMe) => void
}

const ViewReconciledInvoice = (props: ViewReconciledInvoiceProps): null | React.ReactElement => {
  const {
    errors,
    currentInvoice: invoice,
    isHistoryInvoiceViewLoading,
    handleSubmit,
    values,
    getPartyNameById,
    isReadOnly,
    getInvoiceTypeByValue,
    closeInvoice,
    updateBeneficiaries,
    setFieldValue,
  } = props

  const [beneficiariesHash, setBeneficiariesHash] = useState<string | null>(null)

  useEffect(() => {
    setBeneficiariesHash(String(hashCode(JSON.stringify(get(props, 'invoice.beneficiaries', '')))))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = (): void => {
    closeInvoice(values.id, 'history')
  }

  const getBeneficiaries = (): $TSFixMe => get(props, 'values.beneficiaries', [])

  const handleBeneficiaryAmountChange = (beneficiaryIndex: number, e: any): void => {
    const beneficiary = getBeneficiaries().find((v: any, i: any) => i === beneficiaryIndex)
    setFieldValue(`beneficiaries[${beneficiaryIndex}]`, { ...beneficiary, ...{ amount: e.target.value } })
  }

  const handleBeneficiaryOrderChange = (newOrder: any): void => {
    const updatedBeneficiaries = newOrder
      .map((o: any) => getBeneficiaries()[o])
      .filter((b: any) => b !== null && b !== undefined)
    updateBeneficiaries(values.id, updatedBeneficiaries)
  }

  if (isEmpty(values) || !invoice) {
    return null
  }

  const styleNames = classNames('root')

  const invoiceTypeObj = getInvoiceTypeByValue(values.invoiceType || '')
  const invoiceTypeName = pathOr('', ['name'], invoiceTypeObj)

  /**
   * @todo the api should return the value and name
   */
  const haxInvoiceValue = (values.invoiceTypeName || values.invoiceType || '').replace(' ', '')

  const creditNotes = values.creditNotes || []

  return (
    <div className={styleNames}>
      <Helmet>
        <title>reOS | Invoices | History | {`${values.customerName as string} - ${invoiceTypeName as string}`}</title>
      </Helmet>
      <DimmerLoader loading={isHistoryInvoiceViewLoading} style={{ minHeight: '300px' }}>
        <InvoiceCard
          invoice={invoice}
          // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element; invoice: any; contextua... Remove this comment to see the full error message
          contextualMenuOptions={[]}
          onClose={handleClose}
        >
          <section className={styles['invoice-data']}>
            <form onSubmit={handleSubmit}>
              <fieldset disabled={isReadOnly}>
                <Segment.Group>
                  {/** @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element[]; horizontal: true; onC... Remove this comment to see the full error message */}
                  <Segment horizontal style={{ flexWrap: 'nowrap' }}>
                    <Segment.Label icon={<TaskList />} text={values.customerName} secondaryText={invoiceTypeName} />
                    <div className={styles.amount}>
                      <CurrencyField
                        disabled
                        includeVatControl
                        includeVatCalculator={false}
                        vatApplied={values.vatAmount > 0}
                        name="amount"
                        placeholder="Enter amount"
                        value={values.grossAmount}
                      />
                    </div>
                  </Segment>
                  {values.description && (
                    <Segment horizontal>
                      <TextField
                        inputComponent={<TextInput type={TextFieldTypes.text} value={values.description} disabled />}
                        label="Description"
                      />
                    </Segment>
                  )}
                </Segment.Group>

                <PaymentAllocation
                  errors={errors}
                  invoiceType={{ name: values.invoiceType, value: haxInvoiceValue }}
                  invoiceStatus={values.invoiceStatus}
                  balance={values.balance}
                  availableFunds={values.customerAvailableFunds}
                  beneficiaries={getBeneficiaries().map((b: any) => {
                    const partyName = getPartyNameById(pathOr('', ['beneficiary', 'value', 'partyId'], b))
                    return Object.assign({}, b, {
                      beneficiary: {
                        ...b.beneficiary,
                        value: {
                          ...b.beneficiary.value,
                          name: partyName,
                        },
                      },
                      isRemovable: false,
                      isDraggable: false,
                    })
                  })}
                  onNewBeneficiaryAdded={undefined}
                  onNewBeneficiaryAmountChange={handleBeneficiaryAmountChange}
                  onBeneficiariesOrderChange={handleBeneficiaryOrderChange}
                  // @ts-expect-error ts-migrate(2322) FIXME: Type '{ errors: any; invoiceType: { name: any; val... Remove this comment to see the full error message
                  beneficiariesKey={beneficiariesHash}
                  portfolioId={values.portfolioId}
                />

                <Segment.Group>
                  <Segment horizontal>
                    <Segment.Label text="Credit Notes" />
                  </Segment>
                  <CreditNotesList creditNotes={creditNotes} />
                  {!isEmpty(creditNotes) && (
                    <div className={styles['download-credit-note']}>
                      <DownloadCreditNotePdfButton invoiceId={invoice.id} invoiceNumber={invoice.invoiceNumber} />
                    </div>
                  )}
                </Segment.Group>
              </fieldset>

              {invoice?.id && (
                <Segment.Group>
                  <div className={styles['files-section']}>
                    <DocumentInfo className="invoice" classId={invoice.id} modalTitle={invoice.customerName} />
                  </div>
                </Segment.Group>
              )}

              <footer>
                <div className={styles['action-buttons']}>
                  <DownloadInvoicePdfButton invoiceId={invoice.id} invoiceNumber={invoice.invoiceNumber} />
                </div>
              </footer>
            </form>
          </section>
        </InvoiceCard>
      </DimmerLoader>
    </div>
  )
}

export default subscribe(
  [InvoicesProvider],
  ({
    currentInvoice,
    closeInvoice,
    getInvoiceTypeByValue,
    getPartyNameById,
    createPayment,
    nudge,
    updateBeneficiaries,
    isHistoryInvoiceViewLoading,
    isReadOnly,
  }: any) => ({
    currentInvoice,
    closeInvoice,
    getInvoiceTypeByValue,
    getPartyNameById,
    createPayment,
    nudge,
    updateBeneficiaries,
    isHistoryInvoiceViewLoading,
    isReadOnly,
  }),
)(
  withFormik({
    enableReinitialize: true,
    // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
    mapPropsToValues: props => props.currentInvoice,
    handleSubmit: (values, { props, setSubmitting }) => {
      const { id, beneficiaries } = values
      // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
      props.createPayment(
        id,
        beneficiaries.map(({ beneficiary }: any) => ({
          beneficiaryId: beneficiary.value.id,
          amount: beneficiary.value.amount,
        })),
      )
    },
  })(ViewReconciledInvoice as any),
)
